import { Component, ElementRef, HostListener, Renderer2, computed, effect, inject, untracked, viewChild } from "@angular/core";
import { ControlContainer, FormGroupDirective, ReactiveFormsModule, UntypedFormBuilder } from "@angular/forms";
import { CardModalService } from "@shared/card-modal";
import { isJobsBilling } from "@app/views-interfaces/ApplicationType.enum";
import { JobsBillingCard, SaveResultEnum, SupportedLanguage } from "@app/views-interfaces";
import { IframeMessagesService } from "@shared/iframe-messages-service/iframe-messages.service";
import {
    AnalyticsService,
    CardService,
    getAnalyticsExtrasFromCard,
    HelpersService,
    IFirmContactInformationService,
    IFirmContactInformationUpdate,
    IFirmGroupsService,
    MappingOfStatusesService,
    ResponseErrorHandlerService,
    UnassignedUserId,
    UsersService,
    UtilsService,
    WkAutoWidthDirective,
} from "@shared/index";
import { CardDisplayService } from "@shared/card-display";
import { BusinessLogicService } from "@shared/business-logic";
import { type ICardFieldLabelsModel } from "@app/views-interfaces/card-field-labels-model.interface";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DatePickerComponent, DatePickerCustomMessagesComponent } from "@progress/kendo-angular-dateinputs";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { SharedModule } from "@shared/shared.module";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { ICardModalChange } from "@app/shared/interfaces/card-modal-change.interface";
import { IJobsJobModel } from "@app/shared/interfaces/jobs-job-model.interface";
import { JobsAndBillingJobsService } from "@app/shared/jobs-and-billing-jobs-service/jobs-and-billing-jobs.service";
import { JobsAndBillingStatusesService } from "@app/shared/jobs-and-billing-statuses-service/jobs-and-billing-statuses.service";
import { IStatusHistoryRow, StatusHistoryComponent } from "@app/card-modal-component/panels/card-panels/status-history/status-history.component";
import { JobsBillingFollowUpInfoComponent } from "@app/card-modal-component/follow-up-info-component/jobs-billing-follow-up-info/jobs-billing-follow-up-info.component";
import { JobPriority } from "@app/shared/jobs-and-billing-priority";
import { AccordionsService } from "@services/index";
import { JobsBillingCardNotesPanelComponent } from "./jobs-billing-card-notes-panel";
import { CardPanelType } from "@shared/interfaces";
import { GroupsMethodsPanelComponent } from "@app/card-modal-component/panels/card-panels/card-panels";
import { AssigneesJobsPanelComponent } from "@app/card-modal-component/panels/card-panels/assignees-panel";
import { JobsAndBillingCommonService } from "@app/shared/jobs-and-billing-common-service/jobs-and-billing-common.service";

@Component({
    selector: "wk-jobs-billing-card-panel",
    imports: [
        ReactiveFormsModule,
        JumpstartComponentsModule,
        TranslateModule,
        DatePickerComponent,
        DatePickerCustomMessagesComponent,
        SharedModule,
        CommonModule,
        StatusHistoryComponent,
        JobsBillingFollowUpInfoComponent,
        GroupsMethodsPanelComponent,
        JobsBillingCardNotesPanelComponent,
        AssigneesJobsPanelComponent,
        WkAutoWidthDirective,
    ],
    templateUrl: "./jobs-billing-card-panel.component.html",
    styleUrl: "./jobs-billing-card-panel.component.scss",
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class JobsBillingCardPanelComponent {
    protected readonly accordionsService = inject(AccordionsService);
    public readonly cardModalService = inject(CardModalService);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly helpersService = inject(HelpersService);
    private readonly translate = inject(TranslateService);
    private readonly fb = inject(UntypedFormBuilder);
    private readonly renderer = inject(Renderer2);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly cardService = inject(CardService);
    private readonly responseErrorHandler = inject(ResponseErrorHandlerService);
    private readonly businessLogicService = inject(BusinessLogicService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly iFirmContactInformationService = inject(IFirmContactInformationService);
    private readonly jobsAndBillingJobsService = inject(JobsAndBillingJobsService);
    private readonly jobsAndBillingStatusesService = inject(JobsAndBillingStatusesService);
    private readonly usersService = inject(UsersService);
    private readonly iFirmGroupsService = inject(IFirmGroupsService);
    protected readonly CardPanelType = CardPanelType;
    private readonly jobsAndBillingCommonService = inject(JobsAndBillingCommonService);

    protected dateFormat = "MMM dd, yyyy";
    protected cardData: JobsBillingCard = null;
    protected calculatedFields: ICardFieldLabelsModel = null;
    protected statusHistory: IStatusHistoryRow[] = [];

    private readonly snoozeAlertCalendar = viewChild("snoozeAlertCalendar", { read: ElementRef<HTMLElement> });

    protected readonly isAnyGroupEnabled = computed<boolean>(() => {
        return this.iFirmGroupsService.isGroup1JobsEnabled() || this.iFirmGroupsService.isGroup2JobsEnabled();
    });

    protected readonly canEditStartDate = computed<boolean>(() => {
        return this.jobsAndBillingCommonService.hasAccess("target_start_date", this.cardData);
    });

    protected readonly canEditEndDate = computed<boolean>(() => {
        return this.jobsAndBillingCommonService.hasAccess("target_end_date", this.cardData);
    });

    constructor() {
        effect(() => {
            const appType = this.cardModalService.openInApp();
            if (isJobsBilling(appType)) {
                this.handleOpenInJobsBilling();
                untracked(() => this.cardModalService.openInApp.set(0));
            }
        });

        effect(() => {
            if (this.cardModalService.openIniFirmTaxprep()) {
                this.handleOpenIniFirmTaxprep();
                this.cardModalService.openIniFirmTaxprep.set(false);
            }
        });

        effect(() => {
            const card = this.cardModalService.currentCard() as JobsBillingCard;
            if (card) {
                untracked(() => this.initCard(card));
            }
        });

        effect(() => {
            const saveForm = this.cardModalService.saveForm();
            if (saveForm) {
                untracked(() => this.saveForm());
            }
        });

        effect(() => {
            const alert = this.snoozeAlertCalendar();
            const el = alert?.nativeElement;
            const btnSelect = el?.querySelector(".k-input-button");
            if (btnSelect && this.calculatedFields) {
                const existingSpan = btnSelect.querySelector("#snooze_alert_info_text");
                if (existingSpan) {
                    existingSpan.innerHTML = this.calculatedFields.daysSinceInStep + " " + this.translate.instant("CardModal.Days");
                    if (existingSpan.classList.contains("warning")) {
                        this.renderer.removeClass(existingSpan, "warning");
                    }
                    if (existingSpan.classList.contains("danger")) {
                        this.renderer.removeClass(existingSpan, "danger");
                    }
                    if (this.calculatedFields.isStepAlert) {
                        this.renderer.addClass(existingSpan, "danger");
                        this.renderer.setStyle(existingSpan, "cursor", "pointer");
                        this.renderer.setStyle(btnSelect, "cursor", "pointer");
                    } else if (this.calculatedFields.isStepWarning) {
                        this.renderer.addClass(existingSpan, "warning");
                        this.renderer.setStyle(existingSpan, "cursor", "pointer");
                        this.renderer.setStyle(btnSelect, "cursor", "pointer");
                    } else {
                        this.renderer.setStyle(existingSpan, "cursor", "default");
                        this.renderer.setStyle(btnSelect, "cursor", "default");
                    }
                    const existingSvg = btnSelect.querySelector("#svg_icon_snooze_alert");
                    if (existingSvg) {
                        const existingPath = existingSvg.querySelector("path");
                        if (existingPath) {
                            if (this.calculatedFields.isStepAlert) {
                                this.renderer.setAttribute(existingPath, "fill", "#E5202E");
                            } else if (this.calculatedFields.isStepWarning) {
                                this.renderer.setAttribute(existingPath, "fill", "#AF6B00");
                            } else {
                                this.renderer.setAttribute(existingPath, "fill", "#005B92");
                            }
                        }
                    }
                } else {
                    const svgIcon = this.renderer.createElement("svg", "http://www.w3.org/2000/svg");
                    this.renderer.setAttribute(svgIcon, "id", "svg_icon_snooze_alert");
                    this.renderer.setAttribute(svgIcon, "width", "16");
                    this.renderer.setAttribute(svgIcon, "height", "17");
                    this.renderer.setAttribute(svgIcon, "viewBox", "0 0 16 17");
                    this.renderer.setAttribute(svgIcon, "fill", "none");
                    this.renderer.removeAttribute(btnSelect, "title");

                    const path = this.renderer.createElement("path", "http://www.w3.org/2000/svg");
                    this.renderer.setAttribute(path, "fill-rule", "evenodd");
                    this.renderer.setAttribute(path, "clip-rule", "evenodd");
                    this.renderer.setAttribute(
                        path,
                        "d",
                        "M8 0.5C3.6 0.5 0 4.1 0 8.5C0 12.9 3.6 16.5 8 16.5C12.4 16.5 16 12.9 16 8.5C16 4.1 12.4 0.5 8 0.5ZM8 15.4333C4.2 15.4333 1.06667 12.3 1.06667 8.5C1.06667 4.7 4.2 1.56667 8 1.56667C11.8 1.56667 14.9333 4.7 14.9333 8.5C14.9333 12.3 11.8 15.4333 8 15.4333ZM8.53333 3.7H7.46667V9.03333H11.7333V7.96667H8.53333V3.7Z",
                    );
                    if (this.calculatedFields.isStepAlert) {
                        this.renderer.setAttribute(path, "fill", "#E5202E");
                    } else if (this.calculatedFields.isStepWarning) {
                        this.renderer.setAttribute(path, "fill", "#AF6B00");
                    } else {
                        this.renderer.setAttribute(path, "fill", "#005B92");
                    }

                    this.renderer.appendChild(svgIcon, path);
                    this.renderer.appendChild(btnSelect, svgIcon);
                    const span = this.renderer.createElement("span");
                    if (this.calculatedFields.isStepAlert) {
                        this.renderer.addClass(span, "danger");
                        this.renderer.setStyle(span, "cursor", "pointer");
                        this.renderer.setStyle(btnSelect, "cursor", "pointer");
                    } else if (this.calculatedFields.isStepWarning) {
                        this.renderer.addClass(span, "warning");
                        this.renderer.setStyle(span, "cursor", "pointer");
                        this.renderer.setStyle(btnSelect, "cursor", "pointer");
                    } else {
                        this.renderer.setStyle(span, "cursor", "default");
                        this.renderer.setStyle(btnSelect, "cursor", "default");
                    }
                    this.renderer.addClass(span, "calender_info");
                    this.renderer.addClass(span, "snooze-alert-info-text-font");
                    this.renderer.setAttribute(span, "id", "snooze_alert_info_text");

                    const text = this.renderer.createText(this.calculatedFields.daysSinceInStep + " " + this.translate.instant("CardModal.Days"));

                    this.renderer.appendChild(span, text);
                    this.renderer.appendChild(btnSelect, span);
                }
            }
        });

        this.dateFormat = this.helpersService.getLang() == "en-CA" ? "MMM dd, yyyy" : "dd MMM yyyy";
        this.cardModalService.saveChangeEvent = this.saveChanges.bind(this);
    }

    async initCard(cardData: JobsBillingCard) {
        this.cardData = cardData;
        if (!this.cardData) return;

        const step = this.mappingOfStatusesService.getStepFromStatusId(cardData.job?.statusId);
        const statusIds = step?.Statuses?.map((s) => s.TaxReturnStatusId) ?? [];
        this.calculatedFields = this.cardDisplayService.calcCardFieldLabels(cardData, statusIds);
        this.statusHistory = await this.buildStatusHistory(cardData);

        const stepId = step?.Id ?? null;
        this.cardModalService.cardPositionInCol.set({ cardId: cardData.cardId, stepId: stepId });

        this.setFormObject(cardData);
        this.cardModalService.showModal.set(true);
    }

    private async buildStatusHistory(cardData: JobsBillingCard): Promise<IStatusHistoryRow[]> {
        const allStatuses = (await this.jobsAndBillingStatusesService.getJobStatuses()) ?? [];
        const lang = this.helpersService.getLang() as SupportedLanguage;
        const statusById = new Map(allStatuses.map((s) => [s.Id, { name: s.Name[lang], order: s.Order ?? -1 }]));

        return (cardData.statusesHistory ?? [])
            .map((entry) => {
                const status = statusById.get(entry.statusId);
                const user = this.usersService.getUserById(entry.userId);
                return {
                    TaxReturn: status ? { Name: status.name } : undefined,
                    statusDate: entry.statusDate,
                    userName: user ? [user.FirstName, user.LastName].join(" ").trim() : "",
                    hide: false,
                    order: status?.order ?? -1,
                };
            })
            .sort((a, b) => {
                const dateDiff = (b.statusDate ?? 0) - (a.statusDate ?? 0);
                if (dateDiff !== 0) return dateDiff;
                return b.order - a.order;
            });
    }

    private extractPureDateFromEpoch(epoch: number): Date | null {
        if (!epoch) return null;
        const utcDate = new Date(epoch * 1000);

        const year = utcDate.getUTCFullYear();
        const month = utcDate.getUTCMonth(); // Already 0-indexed
        const day = utcDate.getUTCDate();

        return new Date(year, month, day, 0, 0, 0, 0);
    }

    setFormObject(cardData: JobsBillingCard) {
        this.cardModalService.cardModalFormGroup().reset();
        this.cardModalService.cardModalFormGroup.set(
            this.fb.group({
                targetStartDate: [
                    {
                        value: cardData.job?.targetStartDate ? (this.extractPureDateFromEpoch(cardData.job.targetStartDate) ?? "") : "",
                        disabled: !this.canEditStartDate(),
                    },
                ],
                targetEndDate: [
                    {
                        value: cardData.job?.targetEndDate ? (this.extractPureDateFromEpoch(cardData.job.targetEndDate) ?? "") : "",
                        disabled: !this.canEditEndDate(),
                    },
                ],
                quietDate: this.calculatedFields.quietDaysFromNow
                    ? new Date(Date.now() + this.calculatedFields.quietDaysFromNow * 86400000)
                    : new Date(Date.now()),
                quietDays: [this.calculatedFields.quietDaysFromNow],
                documentsReceivedDate: [cardData.documentsReceivedDate ? new Date(cardData.documentsReceivedDate * 1000) : ""],
                jobStatus: [cardData.job?.statusId ?? null],
                priority: [cardData.job?.priority ?? null],
                currentlyResponsible: this.normalizeUserId(cardData.job?.currentlyResponsible),
                jobPartner: this.normalizeUserId(cardData.job?.partner),
                jobManager: this.normalizeUserId(cardData.job?.manager),
                jobOwner: this.normalizeUserId(cardData.job?.owner),
                jobGroup1: this.normalizeGroupId(cardData.job?.group1),
                jobGroup2: this.normalizeGroupId(cardData.job?.group2),
                contactGroup1: this.normalizeGroupId(cardData.contactInformation?.group1),
                contactGroup2: this.normalizeGroupId(cardData.contactInformation?.group2),
                contactPartner: this.normalizeUserId(cardData.contactInformation?.partner),
                contactManager: this.normalizeUserId(cardData.contactInformation?.manager),
                note: cardData.note ?? "",
            }),
        );
    }

    async saveForm() {
        if (this.formChanged() && !this.cardModalService.savingChanges()) {
            this.cardModalService.showLoader.set(true);
            await this.saveChanges(true);
        } else {
            this.cardModalService.closeForm.set(true);
        }
    }

    updateJobStatus(event: { statusId: string }) {
        const formGroup = this.cardModalService.cardModalFormGroup();
        formGroup?.get("jobStatus")?.setValue(event.statusId);
        formGroup?.get("jobStatus")?.markAsDirty();
    }

    updateJobPriority(event: { priority: JobPriority }) {
        const formGroup = this.cardModalService.cardModalFormGroup();
        formGroup?.get("priority")?.setValue(event.priority);
        formGroup?.get("priority")?.markAsDirty();
    }

    async saveChanges(isCloseOnSave: boolean, callback?) {
        const changes = this.getFormChanges();
        if (changes.length > 0) {
            this.cardModalService.savingChanges.set(true);
            const preparedData = await this.getPreparedSaveData(changes);
            const jobsSaveResult = await this.saveJobsChanges(changes, preparedData);
            const contactSaveResult = jobsSaveResult != SaveResultEnum.Failed && (await this.saveContactChanges(changes, preparedData));
            if (jobsSaveResult == SaveResultEnum.Failed) {
                this.responseErrorHandler.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "UpdateJobError",
                });
            } else if (contactSaveResult == SaveResultEnum.Failed) {
                this.responseErrorHandler.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "UpdateContactsError",
                });
            } else {
                await this.saveTrackingChanges(
                    changes,
                    preparedData,
                    isCloseOnSave,
                    this.cardData.cardId,
                    jobsSaveResult != SaveResultEnum.NotApplicable || contactSaveResult != SaveResultEnum.NotApplicable,
                    callback,
                );
            }
            if (jobsSaveResult == SaveResultEnum.Failed || contactSaveResult == SaveResultEnum.Failed) {
                this.cardModalService.showLoader.set(false);
                this.cardModalService.savingChanges.set(false); // Need to set back to false now that we can switch card without closing first
            }
        } else {
            this.router.navigate(["../"], { relativeTo: this.route }); // Form changed, but no changes found (same values), simply close
        }
    }

    getFormChanges(): ICardModalChange[] {
        const changes: ICardModalChange[] = [];
        const formGroup = this.cardModalService.cardModalFormGroup();
        if (formGroup && this.cardData) {
            let docDate = formGroup.get("documentsReceivedDate")?.value;
            try {
                docDate = this.helpersService.convertMillisecondsToSeconds((docDate as Date).getTime());
            } catch {
                docDate = null;
            }
            if (docDate !== (this.cardData.documentsReceivedDate ?? null)) {
                changes.push({ type: "normal", field: "documentsReceivedDate" });
            }

            let startDate = formGroup.get("targetStartDate")?.value;
            try {
                const d = startDate as Date;
                startDate = this.helpersService.convertDateOnlyToSeconds(d);
            } catch {
                startDate = null;
            }

            let currentStartDate: number | null = this.cardData.job?.targetStartDate ?? null;
            try {
                const d = this.extractPureDateFromEpoch(currentStartDate);
                if (d instanceof Date) currentStartDate = this.helpersService.convertDateOnlyToSeconds(d);
                else currentStartDate = null;
            } catch {
                currentStartDate = null;
            }

            if (startDate !== currentStartDate) {
                changes.push({ type: "normal", field: "targetStartDate" });
            }

            let endDate = formGroup.get("targetEndDate")?.value;
            try {
                const d = endDate as Date;
                endDate = this.helpersService.convertDateOnlyToSeconds(d);
            } catch {
                endDate = null;
            }

            let currentEndDate: number | null = this.cardData.job?.targetEndDate ?? null;
            try {
                const d = this.extractPureDateFromEpoch(currentEndDate);
                if (d instanceof Date) currentEndDate = this.helpersService.convertDateOnlyToSeconds(d);
                else currentEndDate = null;
            } catch {
                currentEndDate = null;
            }

            if (endDate !== currentEndDate) {
                changes.push({ type: "normal", field: "targetEndDate" });
            }

            const quietDaysVal = formGroup.get("quietDays")?.value;
            if (quietDaysVal !== this.calculatedFields.quietDaysFromNow) {
                changes.push({ type: "normal", field: "quietDays" });
            }

            const jobStatusVal = formGroup.get("jobStatus")?.value;
            if (jobStatusVal !== (this.cardData.job?.statusId ?? null)) {
                changes.push({ type: "normal", field: "jobStatus" });
            }

            const priorityVal = formGroup.get("priority")?.value;
            if (priorityVal !== (this.cardData.job?.priority ?? null)) {
                changes.push({ type: "normal", field: "priority" });
            }

            const jobCurrentlyResponsible = formGroup.get("currentlyResponsible")?.value;
            if (this.normalizeUserId(jobCurrentlyResponsible) !== this.normalizeUserId(this.cardData.job.currentlyResponsible)) {
                changes.push({ type: "normal", field: "currentlyResponsible" });
            }

            const jobPartner = formGroup.get("jobPartner")?.value;
            if (this.normalizeUserId(jobPartner) !== this.normalizeUserId(this.cardData.job.partner)) {
                changes.push({ type: "normal", field: "jobPartner" });
            }

            const jobManager = formGroup.get("jobManager")?.value;
            if (this.normalizeUserId(jobManager) !== this.normalizeUserId(this.cardData.job.manager)) {
                changes.push({ type: "normal", field: "jobManager" });
            }

            const jobOwner = formGroup.get("jobOwner")?.value;
            if (this.normalizeUserId(jobOwner) !== this.normalizeUserId(this.cardData.job.owner)) {
                changes.push({ type: "normal", field: "jobOwner" });
            }

            const jobGroup1 = formGroup.get("jobGroup1")?.value;
            if (jobGroup1 !== this.normalizeGroupId(this.cardData.job.group1)) {
                changes.push({ type: "normal", field: "jobGroup1" });
            }

            const jobGroup2 = formGroup.get("jobGroup2")?.value;
            if (jobGroup2 !== this.normalizeGroupId(this.cardData.job.group2)) {
                changes.push({ type: "normal", field: "jobGroup2" });
            }

            const contactGroup1 = formGroup.get("contactGroup1")?.value;
            if (contactGroup1 !== this.normalizeGroupId(this.cardData.contactInformation.group1)) {
                changes.push({ type: "normal", field: "contactGroup1" });
            }

            const contactGroup2 = formGroup.get("contactGroup2")?.value;
            if (contactGroup2 !== this.normalizeGroupId(this.cardData.contactInformation.group2)) {
                changes.push({ type: "normal", field: "contactGroup2" });
            }

            const contactPartner = formGroup.get("contactPartner")?.value;
            if (this.normalizeUserId(contactPartner) !== this.normalizeUserId(this.cardData.contactInformation.partner)) {
                changes.push({ type: "normal", field: "contactPartner" });
            }

            const contactManager = formGroup.get("contactManager")?.value;
            if (this.normalizeUserId(contactManager) !== this.normalizeUserId(this.cardData.contactInformation.manager)) {
                changes.push({ type: "normal", field: "contactManager" });
            }

            const note = formGroup.get("note")?.value;
            if (note !== this.cardData.note) {
                changes.push({ type: "normal", field: "note" });
            }
        }
        return changes;
    }

    async getPreparedSaveData(changes: ICardModalChange[]): Promise<JobsBillingCard> {
        const cardFromCache = (await this.cardDisplayService.getFullCard(this.cardModalService.cardId())) as JobsBillingCard;
        const newCardData = UtilsService.recursiveDeepCopy(cardFromCache);
        const formGroup = this.cardModalService.cardModalFormGroup();

        changes.forEach((change) => {
            if (change.field === "documentsReceivedDate") {
                let val = formGroup.get("documentsReceivedDate")?.value;
                try {
                    val = this.helpersService.convertMillisecondsToSeconds((val as Date).getTime());
                } catch {
                    val = null;
                }
                newCardData.documentsReceivedDate = val;
            }
            if (change.field === "targetStartDate") {
                let val = formGroup.get("targetStartDate")?.value;
                try {
                    val = this.helpersService.convertDateOnlyToSeconds(val as Date);
                } catch {
                    val = null;
                }
                newCardData.job.targetStartDate = val;
            }
            if (change.field === "targetEndDate") {
                let val = formGroup.get("targetEndDate")?.value;
                try {
                    val = this.helpersService.convertDateOnlyToSeconds(val as Date);
                } catch {
                    val = null;
                }
                newCardData.job.targetEndDate = val;
            }
            if (change.field === "quietDays") {
                const quietDaysVal = formGroup.get("quietDays")?.value;
                if (quietDaysVal) {
                    newCardData.quiet = Math.floor(this.helpersService.convertMillisecondsToSeconds(Date.now())) + quietDaysVal * 86400;
                } else {
                    newCardData.quiet = null;
                }
            }
            if (change.field === "jobStatus") {
                const userId = this.helpersService.getCurrentUser()?.ifirm_userid;
                this.businessLogicService.setJobStatus(newCardData, formGroup.get("jobStatus")?.value, userId);
            }
            if (change.field === "priority") {
                newCardData.job.priority = formGroup.get("priority")?.value;
            }

            if (change.field === "currentlyResponsible") {
                newCardData.job.currentlyResponsible = formGroup.get("currentlyResponsible")?.value;
            }
            if (change.field === "jobPartner") {
                newCardData.job.partner = formGroup.get("jobPartner")?.value;
            }
            if (change.field === "jobManager") {
                newCardData.job.manager = formGroup.get("jobManager")?.value;
            }
            if (change.field === "jobOwner") {
                newCardData.job.owner = formGroup.get("jobOwner")?.value;
            }

            if (change.field === "jobGroup1") {
                newCardData.job.group1 = String(formGroup.get("jobGroup1")?.value);
            }

            if (change.field === "jobGroup2") {
                newCardData.job.group2 = String(formGroup.get("jobGroup2")?.value);
            }

            if (change.field === "contactGroup1") {
                newCardData.contactInformation.group1 = String(formGroup.get("contactGroup1")?.value);
            }

            if (change.field === "contactGroup2") {
                newCardData.contactInformation.group2 = String(formGroup.get("contactGroup2")?.value);
            }

            if (change.field === "contactPartner") {
                newCardData.contactInformation.partner = formGroup.get("contactPartner")?.value;
            }

            if (change.field === "contactManager") {
                newCardData.contactInformation.manager = formGroup.get("contactManager")?.value;
            }

            if (change.field === "note") {
                newCardData.note = formGroup.get("note")?.value;
            }
        });

        this.businessLogicService.removeCardFields(newCardData);
        return newCardData;
    }

    async saveJobsChanges(changes: ICardModalChange[], preparedData: JobsBillingCard): Promise<SaveResultEnum> {
        if (this.hasJobsChanges(changes)) {
            try {
                const response = await this.jobsAndBillingJobsService.updateJob(
                    preparedData.job.id,
                    await this.getJobsChanges(changes, preparedData),
                );
                if (response && response.length > 0) {
                    return SaveResultEnum.Failed;
                }
                return SaveResultEnum.Success;
            } catch (err) {
                console.error("jobsAndBillingJobsService.updateJob error : " + err);
                return SaveResultEnum.Failed;
            }
        }
        return SaveResultEnum.NotApplicable;
    }

    hasJobsChanges(changes: ICardModalChange[]): boolean {
        return changes.some(
            (x) =>
                x.field === "jobStatus" ||
                x.field === "targetStartDate" ||
                x.field === "targetEndDate" ||
                x.type === "roles" ||
                x.field === "priority" ||
                x.field === "currentlyResponsible" ||
                x.field === "jobPartner" ||
                x.field === "jobManager" ||
                x.field === "jobOwner" ||
                x.field === "jobGroup1" ||
                x.field === "jobGroup2",
        );
    }

    async getJobsChanges(changes: ICardModalChange[], preparedData: JobsBillingCard): Promise<IJobsJobModel | null> {
        let jobsChanges: IJobsJobModel | null = null;
        const formGroup = this.cardModalService.cardModalFormGroup();
        if (formGroup) {
            jobsChanges = { id: preparedData.job.id } as unknown as IJobsJobModel;
            if (changes.some((it) => it.field === "jobStatus")) {
                jobsChanges.jobStatusId = preparedData.job.statusId;
            }
            if (changes.some((it) => it.field === "targetStartDate")) {
                jobsChanges.targetStartDate = preparedData.job.targetStartDate;
            }
            if (changes.some((it) => it.field === "targetEndDate")) {
                jobsChanges.targetEndDate = preparedData.job.targetEndDate;
            }
            if (changes.some((it) => it.field === "priority")) {
                jobsChanges.jobPriority = preparedData.job.priority;
            }
            if (changes.some((it) => it.field === "currentlyResponsible")) {
                const userId =
                    !preparedData.job.currentlyResponsible || preparedData.job.currentlyResponsible == UnassignedUserId
                        ? null
                        : Number(this.usersService.getUserById(preparedData.job.currentlyResponsible)?.IFirmUserId);
                jobsChanges.currentlyResponsible = userId ? String(userId) : null;
            }
            if (changes.some((it) => it.field === "jobPartner")) {
                const userId =
                    !preparedData.job.partner || preparedData.job.partner == UnassignedUserId
                        ? null
                        : Number(this.usersService.getUserById(preparedData.job.partner)?.IFirmUserId);
                jobsChanges.jobPartner = userId ? String(userId) : null;
            }
            if (changes.some((it) => it.field === "jobManager")) {
                const userId =
                    !preparedData.job.manager || preparedData.job.manager == UnassignedUserId
                        ? null
                        : Number(this.usersService.getUserById(preparedData.job.manager)?.IFirmUserId);
                jobsChanges.jobManager = userId ? String(userId) : null;
            }
            if (changes.some((it) => it.field === "jobOwner")) {
                const userId =
                    !preparedData.job.owner || preparedData.job.owner == UnassignedUserId
                        ? null
                        : Number(this.usersService.getUserById(preparedData.job.owner)?.IFirmUserId);
                jobsChanges.jobOwner = userId ? String(userId) : null;
            }
            if (changes.some((it) => it.field === "jobGroup1")) {
                jobsChanges.jobGroup1 = preparedData.job.group1 ?? "-1";
            }
            if (changes.some((it) => it.field === "jobGroup2")) {
                jobsChanges.jobGroup2 = preparedData.job.group2 ?? "-1";
            }
        }
        return jobsChanges;
    }

    roleChanged(roleChange): boolean {
        return (
            roleChange.oldAssignment != roleChange.newAssignment &&
            !(roleChange.oldAssignment === UnassignedUserId && (roleChange.newAssignment === null || roleChange.newAssignment === undefined)) &&
            !(roleChange.newAssignment === UnassignedUserId && (roleChange.oldAssignment === null || roleChange.oldAssignment === undefined))
        );
    }

    hasContactChanges(changes: ICardModalChange[]): boolean {
        return changes.some(
            (x) => x.field === "contactGroup1" || x.field === "contactGroup2" || x.field === "contactPartner" || x.field === "contactManager",
        );
    }

    async getContactChanges(changes: ICardModalChange[], preparedData: JobsBillingCard): Promise<IFirmContactInformationUpdate[]> {
        const contactChanges: IFirmContactInformationUpdate[] = [];

        const formGroup = this.cardModalService.cardModalFormGroup();
        if (formGroup) {
            const contactChange: IFirmContactInformationUpdate = {
                payload: {},
                contactId: Number(this.cardData.contactInformation.iFirmContactId),
            };

            if (this.cardModalService.canContactsViewAddEdit()) {
                if (changes.some((x) => x.field === "contactGroup1")) {
                    contactChange.payload["group_filtering_id1"] = Number(preparedData.contactInformation.group1);
                }

                if (changes.some((x) => x.field === "contactGroup2")) {
                    contactChange.payload["group_filtering_id2"] = Number(preparedData.contactInformation.group2);
                }
            }

            if (this.cardModalService.canEditAssignee()) {
                if (changes.some((it) => it.field === "contactPartner")) {
                    const userId =
                        !preparedData.contactInformation.partner || preparedData.contactInformation.partner == UnassignedUserId
                            ? null
                            : Number(this.usersService.getUserById(preparedData.contactInformation.partner)?.IFirmUserId);
                    contactChange.payload["client_of"] = userId;
                }
                if (changes.some((it) => it.field === "contactManager")) {
                    const userId =
                        !preparedData.contactInformation.manager || preparedData.contactInformation.manager == UnassignedUserId
                            ? null
                            : Number(this.usersService.getUserById(preparedData.contactInformation.manager)?.IFirmUserId);
                    contactChange.payload["manager_id"] = userId;
                }
            }

            if (Object.keys(contactChange.payload).length > 0) {
                contactChanges.push(contactChange);
            }
        }
        return contactChanges;
    }

    async saveContactChanges(changes: ICardModalChange[], preparedData: JobsBillingCard): Promise<SaveResultEnum> {
        if (this.hasContactChanges(changes)) {
            const contactChanges = await this.getContactChanges(changes, preparedData);
            if (contactChanges.length > 0) {
                try {
                    await this.iFirmContactInformationService.updateContacts(contactChanges);
                    return SaveResultEnum.Success;
                } catch (err) {
                    console.error("contact error : " + err);
                    return SaveResultEnum.Failed;
                }
            }
        }
        return SaveResultEnum.NotApplicable;
    }

    async saveTrackingChanges(
        changes: ICardModalChange[],
        preparedData: JobsBillingCard,
        isCloseOnSave: boolean,
        cardIdSaving: string,
        hasIFirmChanges: boolean,
        callback?: () => void,
    ) {
        this.cardService.updateCard(cardIdSaving, { card: preparedData }).subscribe({
            next: () => {
                for (const change of changes) {
                    if (change.type == "normal") {
                        if (change.field == "jobStatus") {
                            this.cardData.job.statusId = preparedData.job.statusId;
                            this.cardData.job.statusDate = preparedData.job.statusDate;
                            this.cardData.job.statusUserId = preparedData.job.statusUserId;
                            this.cardData.statusesHistory = preparedData.statusesHistory;
                        } else if (change.field == "priority") {
                            this.cardData.job.priority = preparedData.job.priority;
                        } else if (change.field === "targetStartDate" || change.field === "targetEndDate") {
                            this.cardData.job[change.field] = preparedData.job[change.field];
                        } else if (change.field === "jobGroup1") {
                            this.cardData.job.group1 = preparedData.job.group1;
                        } else if (change.field === "jobGroup2") {
                            this.cardData.job.group2 = preparedData.job.group2;
                        } else if (change.field === "jobPartner") {
                            this.cardData.job.partner = preparedData.job.partner;
                        } else if (change.field === "jobManager") {
                            this.cardData.job.manager = preparedData.job.manager;
                        } else if (change.field === "jobOwner") {
                            this.cardData.job.owner = preparedData.job.owner;
                        } else if (change.field === "currentlyResponsible") {
                            this.cardData.job.currentlyResponsible = preparedData.job.currentlyResponsible;
                        } else if (change.field === "contactGroup1") {
                            this.cardData.contactInformation.group1 = preparedData.contactInformation.group1;
                        } else if (change.field === "contactGroup2") {
                            this.cardData.contactInformation.group2 = preparedData.contactInformation.group2;
                        } else if (change.field === "contactPartner") {
                            this.cardData.contactInformation.partner = preparedData.contactInformation.partner;
                        } else if (change.field === "contactManager") {
                            this.cardData.contactInformation.manager = preparedData.contactInformation.manager;
                        } else {
                            this.cardData[change.field] = preparedData[change.field];
                        }
                    } else if (change.type == "roles") {
                        // TODO : card does not have role user mapping
                        //this.cardData.roleUserMapping = preparedData.roleUserMapping;
                    } else {
                        this.cardData[change.type] = preparedData[change.type];
                    }
                }
                // Revalidate to not override cached card
                if (this.cardData?._ts === this.cardDisplayService.getCardById(this.cardData.cardId)?._ts) {
                    this.cardDisplayService.updateCardById(cardIdSaving, UtilsService.recursiveDeepCopy(this.cardData), false);
                }
                const changeKeys: string[] = changes.map((c) => ("field" in c && c.field ? c.field : c.type));
                if (this.wasUnassigned(this.cardData, preparedData)) {
                    changeKeys.push("wasUnassigned");
                }
                this.trackCardSaveAnalytics(changeKeys);
                if (callback) {
                    callback();
                }
                if (isCloseOnSave) {
                    this.router.navigate(["../"], { relativeTo: this.route });
                }
                this.cardModalService.showLoader.set(false);
                this.cardModalService.savingChanges.set(false); // Need to set back to false now that we can switch card without closing first
            },
            error: (error) => {
                if (error?.status === 429) {
                    this.responseErrorHandler.showPopup({
                        typeLayout: "ERROR",
                        messageCode: "429TooManyRequests",
                    });
                } else {
                    this.responseErrorHandler.showPopup({
                        typeLayout: "ERROR",
                        messageCode: hasIFirmChanges ? "UpdateTrackingWithIFirmCardError" : "UpdateTrackingCardError",
                    });
                }
                this.cardModalService.showLoader.set(false);
                this.cardModalService.savingChanges.set(false); // Need to set back to false now that we can switch card without closing first
            },
        });
    }

    /** Normalizes all None representations (null, undefined, "", "-1") for group ID fields to -1. */
    private normalizeGroupId(value: string | null | undefined): number {
        if (value == null || value === "" || value === "-1") return -1;
        const n = Number(value);
        return Number.isNaN(n) ? -1 : n;
    }

    /** Normalizes all None representations (null, undefined, "", "-1") for user ID fields to null. */
    private normalizeUserId(value: string | null | undefined): string | null {
        if (value == null || value === "" || value === "-1") return null;
        return value;
    }

    public onChange(value: Date): void {
        const newSnoozeValue: number = this.helpersService.daysDifferenceUnix(this.helpersService.convertMillisecondsToSeconds(value.getTime()));
        this.cardModalService
            .cardModalFormGroup()
            ?.get("quietDays")
            ?.setValue(Math.abs(newSnoozeValue) + 1);
        this.cardModalService.cardModalFormGroup().get("quietDays")?.markAsDirty();
    }

    formChanged() {
        return this.cardModalService.cardModalFormGroup() ? this.cardModalService.cardModalFormGroup().dirty : false;
    }

    public disabledDates = (date: Date): boolean => {
        const today = new Date();
        return date < today;
    };

    public disabledStartDates = (date: Date): boolean => {
        const endDateValue = this.cardModalService.cardModalFormGroup()?.get("targetEndDate")?.value;
        if (endDateValue instanceof Date) {
            const endDate = new Date(endDateValue);
            endDate.setHours(0, 0, 0, 0);
            const d = new Date(date);
            d.setHours(0, 0, 0, 0);
            return d >= endDate;
        }
        return false;
    };

    public disabledEndDates = (date: Date): boolean => {
        const startDateValue = this.cardModalService.cardModalFormGroup()?.get("targetStartDate")?.value;
        if (startDateValue instanceof Date) {
            const startDate = new Date(startDateValue);
            startDate.setHours(0, 0, 0, 0);
            const d = new Date(date);
            d.setHours(0, 0, 0, 0);
            return d <= startDate;
        }
        return false;
    };

    @HostListener("click", ["$event"])
    onBlurEvent(event: MouseEvent) {
        if (event.target instanceof HTMLElement) {
            if (event.target.id !== "snooze_alert_info_text") {
                this.snoozeAlertCalendar()?.nativeElement?.classList.remove("k-focus");
            }
        }
    }

    private handleOpenInJobsBilling(): void {
        const card = this.cardModalService.currentCard() as JobsBillingCard;
        this.analyticsService.track("Open Jobs & Billing link", getAnalyticsExtrasFromCard(card));
        window.open(this.iframeMessagesService.messagesOrigin + "/#/apm/job_manager-details?mode=edit&id=" + card?.job?.id, "_blank");
        this.cardModalService.closeForm.set(true);
    }

    private handleOpenIniFirmTaxprep(): void {
        const card = this.cardModalService.currentCard() as JobsBillingCard;
        const productType = String(card?.return?.productType).substring(0, 2).replace("t4", "tf");
        window.open(
            `${this.iframeMessagesService.messagesOrigin}/taxcan/products/${productType}/documentRedirect?documentId=${card.return.documentId}&returnId=${card.return.returnId ?? 0}`,
            "_blank",
        );
    }

    private trackCardSaveAnalytics(changes: string[]): void {
        const hasChange = (key: string) => changes.includes(key);
        this.analyticsService.track("Saved a card - Jobs & Billing", {
            cardId: this.cardData.cardId,
            ctx_jobId: this.cardData.job?.id,
            target_start_dateChanged: hasChange("targetStartDate") ? "Yes" : "No",
            target_end_dateChanged: hasChange("targetEndDate") ? "Yes" : "No",
            date_receivedChanged: hasChange("documentsReceivedDate") ? "Yes" : "No",
            job_statusChanged: hasChange("jobStatus") ? "Yes" : "No",
            priorityChanged: hasChange("priority") ? "Yes" : "No",
            tracking_NotesAdded: hasChange("note") ? "Yes" : "No",
            contact_group1Changed: hasChange("contactGroup1") ? "Yes" : "No",
            contact_group2Changed: hasChange("contactGroup2") ? "Yes" : "No",
            contact_partnerChanged: hasChange("contactPartner") ? "Yes" : "No",
            contact_managerChanged: hasChange("contactManager") ? "Yes" : "No",
            currently_responsibleChanged: hasChange("currentlyResponsible") ? "Yes" : "No",
            job_partnerChanged: hasChange("jobPartner") ? "Yes" : "No",
            job_managerChanged: hasChange("jobManager") ? "Yes" : "No",
            job_ownerChanged: hasChange("jobOwner") ? "Yes" : "No",
            job_group1Changed: hasChange("jobGroup1") ? "Yes" : "No",
            job_group2Changed: hasChange("jobGroup2") ? "Yes" : "No",
            snoozeChanged: hasChange("quietDays") ? "Yes" : "No",
            fromDragAndDrop: "No",
            wasUnassigned: hasChange("wasUnassigned") ? "Yes" : "No",
        });
    }

    private wasUnassigned(oldCard: JobsBillingCard, newCard: JobsBillingCard): boolean {
        const oldResponsible = oldCard?.job?.currentlyResponsible;
        const newResponsible = newCard?.job?.currentlyResponsible;
        return (!oldResponsible || oldResponsible === UnassignedUserId) && !!newResponsible && newResponsible !== UnassignedUserId;
    }
}
